import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import cccApi, { RecuperacaoHistoryEvent, RecuperacaoMarcacao } from '../api/cccApi';
import {
  ALL_WORKSPACE_SUBSTAGES,
  SUBSTAGE_AGUARDAR_PROPOSTA_BO,
  SUBSTAGE_CONTRATO_SUBMETIDO,
  normalizeWorkspaceSubstage,
  subsectionLabel,
} from '../config/recuperacaoWorkspace';
import {
  FIDELIZADOS_WARNING_TEXT,
  isFidelizadosDadosIncomplete,
  isFidelizadosSubstage,
} from '../config/fidelizadosDados';
import { isAssinadoSubstage } from '../config/assinadoDados';
import { isEnvioPropostaSubstage } from '../config/envioProposta';
import {
  isNaoAtendidoSubstage,
  isTentativasContactoIncomplete,
  TENTATIVAS_CONTACTO_WARNING_TEXT,
} from '../config/tentativasContacto';
import {
  getMotivoVariantForSubstage,
  isMotivoTabSubstage,
  isWorkspaceMotivoIncomplete,
  MOTIVO_WARNING_TEXT,
  PENDENTE_MOTIVO_WARNING_TEXT,
  SEM_INTERESSE_MOTIVO_WARNING_TEXT,
  WorkspaceMotivoVariant,
} from '../config/workspaceMotivos';
import { RecuperacaoMotivoWarningIcon } from './RecuperacaoMotivoWarning';
import RecuperacaoAssinadoForm from './RecuperacaoAssinadoForm';
import RecuperacaoEnvioPropostaForm from './RecuperacaoEnvioPropostaForm';
import RecuperacaoFidelizadosForm from './RecuperacaoFidelizadosForm';
import RecuperacaoTentativasContactoForm from './RecuperacaoTentativasContactoForm';
import RecuperacaoWorkspaceMotivo from './RecuperacaoWorkspaceMotivo';
import CccSolicitarPropostaOverlay from './CccSolicitarPropostaOverlay';
import CccSubmeterContratoRecuperacaoOverlay from './CccSubmeterContratoRecuperacaoOverlay';
import { isValidMarcacaoId } from '../config/prospeccaoMarcacao';
import RecuperacaoComercialRequests from './RecuperacaoComercialRequests';
import RecuperacaoServiceFiles from './RecuperacaoServiceFiles';
import {
  buildCallcenterLeadDetailsLayout,
  buildComercialLeadDetailsLayout,
  buildPedidoPropostaLeadDetailsLayout,
  getWorkspaceSubstage,
  hasComercialProposal,
  LeadDetailGroup,
  LeadDetailRow,
  LeadDetailsLayout,
} from '../utils/recuperacaoMarcacaoDisplay';
import {
  buildCompletedProposalsForMarcacao,
  hasCccPedidoProposta,
} from '../utils/proposalRequestsDisplay';
import { useCccAuth } from '../contexts/CccAuthContext';

export type RecuperacaoNote = {
  text: string;
  at: string;
  by_name?: string;
  by_id?: number;
};

type InfoTab =
  | 'motivo'
  | 'fidelizados'
  | 'tentativas_contacto'
  | 'envio_proposta'
  | 'assinado'
  | 'historico'
  | 'callcenter'
  | 'comercial'
  | 'pedido';

const formatDateTime = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const canAccessHistoricoTab = (user?: Record<string, unknown> | null) => {
  const role = String(user?.role || '').trim().toLowerCase();
  const tenant = String(user?.tenant || '').trim().toLowerCase();
  const businessArea = String(user?.business_area || '').trim().toLowerCase();
  if (role.includes('admin') || tenant === 'admin' || businessArea === 'admin') return true;
  return role.includes('gerente') || role.includes('manager');
};

const historyStatusLabel = (value?: string | null) => {
  const normalized = normalizeWorkspaceSubstage(String(value || ''));
  return subsectionLabel(normalized);
};

const historyEventSummary = (event: RecuperacaoHistoryEvent) => {
  if (event.event_type === 'alteracao_comercial') {
    const fromName = event.details?.old_user_name || 'Sem comercial';
    const toName = event.details?.new_user_name || '—';
    return `Comercial: ${fromName} → ${toName}`;
  }
  if (event.event_type === 'status_update') {
    const fromStatus = event.details?.old_substage ? historyStatusLabel(event.details.old_substage) : null;
    const toStatus = event.details?.new_substage ? historyStatusLabel(event.details.new_substage) : null;
    return fromStatus ? `Estado: ${fromStatus} -> ${toStatus || 'Sem estado'}` : `Novo estado: ${toStatus || 'Sem estado'}`;
  }
  if (event.event_type === 'solicitar_proposta') {
    const toStatus = event.details?.new_substage ? historyStatusLabel(event.details.new_substage) : null;
    return toStatus ? `Solicitar Proposta iniciado (${toStatus})` : 'Solicitar Proposta iniciado';
  }
  if (event.event_type === 'workspace_claim') {
    return 'Marcação iniciada na área de trabalho de Recuperação';
  }
  return 'Registo de criação da marcação';
};

const historyEventTypeLabel = (eventType: RecuperacaoHistoryEvent['event_type']) => {
  if (eventType === 'alteracao_comercial') return 'Alteração do Comercial';
  if (eventType === 'solicitar_proposta') return 'Solicitar Proposta';
  if (eventType === 'status_update') return 'Atualização do Estado';
  return 'Início';
};

const historyEventStatusLabels = (event: RecuperacaoHistoryEvent) => {
  const out: string[] = [];
  if (event.details?.old_substage) out.push(historyStatusLabel(event.details.old_substage));
  if (event.details?.new_substage) out.push(historyStatusLabel(event.details.new_substage));
  return Array.from(new Set(out));
};

type HistoryCategory =
  | 'all'
  | 'solicitar_proposta'
  | 'iniciar_caso'
  | 'status_update'
  | 'alteracao_comercial';

const historyEventCategory = (event: RecuperacaoHistoryEvent): Exclude<HistoryCategory, 'all'> => {
  if (event.event_type === 'alteracao_comercial') return 'alteracao_comercial';
  if (event.event_type === 'solicitar_proposta') return 'solicitar_proposta';
  if (event.event_type === 'status_update') return 'status_update';
  return 'iniciar_caso';
};

const historyCategoryLabel = (category: Exclude<HistoryCategory, 'all'>) => {
  if (category === 'alteracao_comercial') return 'Alteração do Comercial';
  if (category === 'solicitar_proposta') return 'Solicitar Proposta';
  if (category === 'status_update') return 'Atualização do Estado';
  return 'Início';
};

const LeadFactRows: React.FC<{ rows: LeadDetailRow[] }> = ({ rows }) => (
  <dl className="rec-lead-facts">
    {rows.map((row) => (
      <div key={row.key} className="rec-lead-fact">
        <dt>{row.label}</dt>
        <dd>
          {row.href ? (
            <a href={row.href} target="_blank" rel="noopener noreferrer">
              {row.value}
            </a>
          ) : (
            row.value
          )}
        </dd>
      </div>
    ))}
  </dl>
);

const LeadDetailCard: React.FC<{ group: LeadDetailGroup }> = ({ group }) => (
  <section className="rec-lead-detail-group">
    <h4 className="rec-lead-detail-group-title">{group.title}</h4>
    <LeadFactRows rows={group.rows} />
    {group.files && group.files.length > 0 ? (
      <RecuperacaoServiceFiles files={group.files} leadId={group.leadId ?? null} />
    ) : null}
  </section>
);

const servicoItemCount = (group: LeadDetailGroup) =>
  group.rows.length + (group.files?.length ?? 0);

const ServicoDetailBody: React.FC<{ group: LeadDetailGroup; showServiceLabel?: boolean }> = ({
  group,
  showServiceLabel,
}) => (
  <>
    {showServiceLabel ? (
      <LeadFactRows rows={[{ key: 'servico', label: 'Serviço', value: group.title }]} />
    ) : null}
    <LeadFactRows rows={group.rows} />
    {group.files && group.files.length > 0 ? (
      <RecuperacaoServiceFiles files={group.files} leadId={group.leadId ?? null} />
    ) : null}
  </>
);

type LeadDetailsContentProps = {
  layout: LeadDetailsLayout;
  expandedServicos: Record<string, boolean>;
  onToggleServico: (groupId: string) => void;
  emptyMessage: string;
};

const LeadDetailsContent: React.FC<LeadDetailsContentProps> = ({
  layout,
  expandedServicos,
  onToggleServico,
  emptyMessage,
}) => {
  const gestaoNotesBlock =
    layout.gestaoNotes && layout.gestaoNotes.length > 0 ? (
      <div className="rec-lead-gestao-notes">
        {layout.gestaoNotes.map((group) => (
          <LeadDetailCard key={group.id} group={group} />
        ))}
      </div>
    ) : null;

  if (layout.comercialRequests && layout.comercialRequests.length > 0) {
    return (
      <>
        {gestaoNotesBlock}
        <RecuperacaoComercialRequests
          requests={layout.comercialRequests}
          expandedServicos={expandedServicos}
          onToggleServico={onToggleServico}
        />
      </>
    );
  }

  if (gestaoNotesBlock) {
    return gestaoNotesBlock;
  }

  const multiServico = layout.servicos.length > 1;
  const hasDetails = layout.empresa || layout.servicos.length > 0;
  const splitColumns = !!layout.empresa && layout.servicos.length > 0;

  if (!hasDetails) {
    return <p className="rec-lead-panel-empty">{emptyMessage}</p>;
  }

  return (
    <div className={`rec-lead-details${splitColumns ? ' rec-lead-details--split' : ''}`}>
      {layout.empresa && (
        <div className="rec-lead-details-col rec-lead-details-col--empresa">
          <LeadDetailCard group={layout.empresa} />
        </div>
      )}

      {layout.servicos.length > 0 && (
        <div className="rec-lead-details-col rec-lead-details-col--servicos">
          <div className="rec-lead-detail-group rec-lead-servicos-panel">
            <h4 className="rec-lead-detail-group-title">
              {multiServico ? 'Detalhes dos Serviços' : 'Detalhes do Serviço'}
            </h4>
            <div className="rec-lead-servicos-block">
              {layout.servicos.map((group) =>
                multiServico ? (
                  <section
                    key={group.id}
                    className={`rec-lead-servico-accordion ${expandedServicos[group.id] ? 'is-open' : ''}`}
                  >
                    <button
                      type="button"
                      className="rec-lead-servico-trigger"
                      onClick={() => onToggleServico(group.id)}
                      aria-expanded={!!expandedServicos[group.id]}
                    >
                      <span className="rec-lead-servico-chevron" aria-hidden>
                        {expandedServicos[group.id] ? '▼' : '▶'}
                      </span>
                      <span className="rec-lead-servico-trigger-title">{group.title}</span>
                      <span className="rec-lead-servico-trigger-meta">
                        {servicoItemCount(group)} {servicoItemCount(group) === 1 ? 'item' : 'itens'}
                      </span>
                    </button>
                    {expandedServicos[group.id] && (
                      <div className="rec-lead-servico-body">
                        <ServicoDetailBody group={group} />
                      </div>
                    )}
                  </section>
                ) : (
                  <ServicoDetailBody key={group.id} group={group} showServiceLabel />
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type Props = {
  marcacao: RecuperacaoMarcacao;
  onCollapse: () => void;
  onSubstageChange: (substage: string) => void | Promise<void>;
  onWorkspaceNavigate?: (substage: string) => void;
  onRetroceder: () => void;
  onReverterParaComercial?: () => void;
  onAddNote: (text: string) => Promise<void>;
  onMarcacaoRefresh?: () => void | Promise<void>;
  onMarcacaoPatched?: (marcacao: RecuperacaoMarcacao) => void;
  onCreateCalendarEvent?: () => void;
  savingNote?: boolean;
  initialInfoTab?: InfoTab;
};

const RecuperacaoLeadPanel: React.FC<Props> = ({
  marcacao,
  onCollapse,
  onSubstageChange,
  onWorkspaceNavigate,
  onRetroceder,
  onReverterParaComercial,
  onAddNote,
  onMarcacaoRefresh,
  onMarcacaoPatched,
  onCreateCalendarEvent,
  savingNote,
  initialInfoTab,
}) => {
  const { user } = useCccAuth();
  const [changingSubstage, setChangingSubstage] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [infoTab, setInfoTab] = useState<InfoTab>('callcenter');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<RecuperacaoHistoryEvent[]>([]);
  const [historyTypeFilter, setHistoryTypeFilter] = useState<HistoryCategory>('all');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<string>('all');
  const [activeHistoryKey, setActiveHistoryKey] = useState<string | null>(null);
  const [expandedServicos, setExpandedServicos] = useState<Record<string, boolean>>({});
  const [showSolicitarProposta, setShowSolicitarProposta] = useState(false);
  const [showRefazerProposta, setShowRefazerProposta] = useState(false);
  const [refazerMarcacao, setRefazerMarcacao] = useState<RecuperacaoMarcacao | null>(null);
  const [preparingProposta, setPreparingProposta] = useState(false);
  const [preparingRefazer, setPreparingRefazer] = useState(false);
  const [showSubmeterContrato, setShowSubmeterContrato] = useState(false);
  const [preparingContrato, setPreparingContrato] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const substage = getWorkspaceSubstage(marcacao);
  const normalizedSubstage = normalizeWorkspaceSubstage(substage);
  const motivoVariant: WorkspaceMotivoVariant | null = getMotivoVariantForSubstage(normalizedSubstage);
  const showMotivoTab = isMotivoTabSubstage(normalizedSubstage);
  const showFidelizadosTab = isFidelizadosSubstage(normalizedSubstage);
  const showEnvioPropostaTab = isEnvioPropostaSubstage(normalizedSubstage);
  const showAssinadoTab = isAssinadoSubstage(normalizedSubstage);
  const showTentativasTab = isNaoAtendidoSubstage(normalizedSubstage);
  const showWorkspaceFormTab = showMotivoTab || showFidelizadosTab || showTentativasTab;
  const envioPropostaData = marcacao.recuperacao_claim?.envio_proposta;
  const assinadoData = marcacao.recuperacao_claim?.assinado_dados;
  const motivoData =
    motivoVariant === 'pendente'
      ? marcacao.recuperacao_claim?.pendente_motivo
      : motivoVariant === 'sem_interesse'
        ? marcacao.recuperacao_claim?.sem_interesse_motivo
        : null;
  const motivoIncomplete = isWorkspaceMotivoIncomplete(motivoData);
  const fidelizadosData = marcacao.recuperacao_claim?.fidelizados_dados;
  const fidelizadosIncomplete = isFidelizadosDadosIncomplete(fidelizadosData);
  const tentativasData = marcacao.recuperacao_claim?.tentativas_contacto;
  const tentativasIncomplete = isTentativasContactoIncomplete(tentativasData);
  const workspaceFormIncomplete =
    motivoIncomplete || fidelizadosIncomplete || tentativasIncomplete;
  const workspaceFormWarnTitle = showTentativasTab
    ? TENTATIVAS_CONTACTO_WARNING_TEXT
    : showFidelizadosTab
    ? FIDELIZADOS_WARNING_TEXT
    : motivoVariant === 'pendente'
      ? PENDENTE_MOTIVO_WARNING_TEXT
      : motivoVariant === 'sem_interesse'
        ? SEM_INTERESSE_MOTIVO_WARNING_TEXT
      : MOTIVO_WARNING_TEXT;
  const notes = (marcacao.recuperacao_claim?.notes || []) as RecuperacaoNote[];
  const id = isValidMarcacaoId(marcacao.marcacao_id)
    ? marcacao.marcacao_id.trim()
    : marcacao.id != null && isValidMarcacaoId(String(marcacao.id))
      ? String(marcacao.id).trim()
      : '';
  const isMine = marcacao.is_mine === true;
  const canViewHistory = canAccessHistoricoTab(user);
  const pedidoExists = hasCccPedidoProposta((marcacao.form_data || {}) as Record<string, unknown>);

  const callcenterLayout = useMemo(() => buildCallcenterLeadDetailsLayout(marcacao), [marcacao]);
  const comercialLayout = useMemo(() => buildComercialLeadDetailsLayout(marcacao), [marcacao]);
  const pedidoLayout = useMemo(() => buildPedidoPropostaLeadDetailsLayout(marcacao), [marcacao]);
  const proposalExists =
    hasComercialProposal(marcacao) || (comercialLayout.gestaoNotes?.length ?? 0) > 0;
  const activeLayout =
    infoTab === 'pedido' ? pedidoLayout : infoTab === 'comercial' ? comercialLayout : callcenterLayout;
  const historyStatusOptions = useMemo(() => {
    const set = new Set<string>();
    historyEvents.forEach((event) => {
      historyEventStatusLabels(event).forEach((status) => set.add(status));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [historyEvents]);
  const filteredHistoryEvents = useMemo(
    () =>
      [...historyEvents]
        .sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1))
        .filter((event) => {
          if (historyTypeFilter !== 'all' && historyEventCategory(event) !== historyTypeFilter) return false;
          if (historyStatusFilter === 'all') return true;
          const statuses = historyEventStatusLabels(event);
          return statuses.includes(historyStatusFilter);
        }),
    [historyEvents, historyTypeFilter, historyStatusFilter]
  );
  const timelineRows = useMemo(() => {
    const chunkSize = 14;
    const rows: RecuperacaoHistoryEvent[][] = [];
    for (let i = 0; i < filteredHistoryEvents.length; i += chunkSize) {
      rows.push(filteredHistoryEvents.slice(i, i + chunkSize));
    }
    return rows;
  }, [filteredHistoryEvents]);
  const activeHistoryEvent = useMemo(() => {
    if (!activeHistoryKey) return filteredHistoryEvents[0] || null;
    const [ts, type, rowIdxRaw, idxRaw] = activeHistoryKey.split('|');
    const rowIdx = Number(rowIdxRaw);
    const idx = Number(idxRaw);
    const row = timelineRows[rowIdx];
    const item = row?.[idx];
    if (!item) return filteredHistoryEvents[0] || null;
    if (item.timestamp !== ts || item.event_type !== type) return filteredHistoryEvents[0] || null;
    return item;
  }, [activeHistoryKey, filteredHistoryEvents, timelineRows]);

  useEffect(() => {
    setExpandedServicos({});
    setShowSolicitarProposta(false);
    setShowRefazerProposta(false);
    setRefazerMarcacao(null);
    setShowSubmeterContrato(false);
    if (initialInfoTab === 'historico' && canViewHistory) {
      setInfoTab('historico');
      return;
    }
    setInfoTab(
      showAssinadoTab
        ? 'assinado'
        : showEnvioPropostaTab
          ? 'envio_proposta'
          : showTentativasTab
            ? 'tentativas_contacto'
            : showFidelizadosTab
              ? 'fidelizados'
              : showMotivoTab
                ? 'motivo'
                : 'callcenter'
    );
  }, [
    id,
    showMotivoTab,
    showFidelizadosTab,
    showTentativasTab,
    showEnvioPropostaTab,
    showAssinadoTab,
    initialInfoTab,
    canViewHistory,
  ]);

  useEffect(() => {
    if (!showAssinadoTab && infoTab === 'assinado') {
      setInfoTab(
        showEnvioPropostaTab
          ? 'envio_proposta'
          : showTentativasTab
            ? 'tentativas_contacto'
            : showFidelizadosTab
              ? 'fidelizados'
              : showMotivoTab
                ? 'motivo'
                : 'callcenter'
      );
    }
    if (!showEnvioPropostaTab && infoTab === 'envio_proposta') {
      setInfoTab(
        showAssinadoTab
          ? 'assinado'
          : showTentativasTab
            ? 'tentativas_contacto'
            : showFidelizadosTab
              ? 'fidelizados'
              : showMotivoTab
                ? 'motivo'
                : 'callcenter'
      );
    }
    if (!showTentativasTab && infoTab === 'tentativas_contacto') {
      setInfoTab(
        showAssinadoTab
          ? 'assinado'
          : showEnvioPropostaTab
            ? 'envio_proposta'
            : showFidelizadosTab
              ? 'fidelizados'
              : showMotivoTab
                ? 'motivo'
                : 'callcenter'
      );
    }
    if (!showMotivoTab && infoTab === 'motivo') {
      setInfoTab(
        showAssinadoTab
          ? 'assinado'
          : showEnvioPropostaTab
            ? 'envio_proposta'
            : showTentativasTab
              ? 'tentativas_contacto'
              : showFidelizadosTab
                ? 'fidelizados'
                : 'callcenter'
      );
    }
    if (!showFidelizadosTab && infoTab === 'fidelizados') {
      setInfoTab(
        showAssinadoTab
          ? 'assinado'
          : showEnvioPropostaTab
            ? 'envio_proposta'
            : showTentativasTab
              ? 'tentativas_contacto'
              : showMotivoTab
                ? 'motivo'
                : 'callcenter'
      );
    }
    if (!canViewHistory && infoTab === 'historico') {
      setInfoTab('callcenter');
    }
  }, [
    showMotivoTab,
    showFidelizadosTab,
    showTentativasTab,
    showEnvioPropostaTab,
    showAssinadoTab,
    canViewHistory,
    infoTab,
  ]);

  useEffect(() => {
    if (infoTab === 'comercial' && !proposalExists) {
      setInfoTab(pedidoExists ? 'pedido' : 'callcenter');
    }
    if (infoTab === 'pedido' && !pedidoExists) {
      setInfoTab(proposalExists ? 'comercial' : 'callcenter');
    }
  }, [infoTab, proposalExists, pedidoExists]);

  useEffect(() => {
    if (!canViewHistory || infoTab !== 'historico') return;
    let cancelled = false;
    setHistoryLoading(true);
    cccApi
      .getRecuperacaoMarcacaoHistory(id)
      .then((res) => {
        if (!cancelled) {
          setHistoryEvents(Array.isArray(res.events) ? res.events : []);
          setHistoryTypeFilter('all');
          setHistoryStatusFilter('all');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Erro ao carregar histórico da marcação';
        message.error(msg);
        setHistoryEvents([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canViewHistory, id, infoTab, marcacao.recuperacao_claim?.claimed_by, historyRefreshKey]);

  useEffect(() => {
    if (filteredHistoryEvents.length === 0) {
      setActiveHistoryKey(null);
      return;
    }
    if (!activeHistoryKey) {
      const first = filteredHistoryEvents[0];
      setActiveHistoryKey(`${first.timestamp}|${first.event_type}|0|0`);
    }
  }, [filteredHistoryEvents, activeHistoryKey]);

  const handleSolicitarProposta = async () => {
    if (!isMine) {
      message.warning('Inicie a marcação na sua área de trabalho antes de solicitar proposta.');
      return;
    }
    if (!id) {
      message.error('Marcação inválida — não foi possível abrir Solicitar Proposta.');
      return;
    }
    setPreparingProposta(true);
    try {
      const res = await cccApi.prepareSolicitarProposta(id);
      if (!res.success) {
        message.error(
          res.message || 'Não foi possível atualizar o estado para "A aguardar Proposta BO".'
        );
        return;
      }
      if (res.marcacao) {
        onMarcacaoPatched?.(res.marcacao);
      } else {
        await onMarcacaoRefresh?.();
        onWorkspaceNavigate?.(SUBSTAGE_AGUARDAR_PROPOSTA_BO);
      }
      setHistoryRefreshKey((k) => k + 1);
      setShowSolicitarProposta(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao atualizar estado antes de abrir Solicitar Proposta';
      message.error(msg);
    } finally {
      setPreparingProposta(false);
    }
  };

  const handleRefazerProposta = async () => {
    if (!isMine) {
      message.warning('Inicie a marcação na sua área de trabalho antes de refazer proposta.');
      return;
    }
    if (!id) {
      message.error('Marcação inválida — não foi possível abrir Refazer.');
      return;
    }
    setPreparingRefazer(true);
    try {
      const data = await cccApi.getRecuperacaoMarcacao(id);
      if (!data?.success || !data.marcacao) {
        message.error('Não foi possível carregar a marcação para Refazer.');
        return;
      }
      const completed = buildCompletedProposalsForMarcacao({
        marcacao_id: id,
        nome_empresa: data.marcacao.nome_empresa,
        form_data: (data.marcacao.form_data || {}) as Record<string, unknown>,
      });
      if (completed.length === 0) {
        message.info('Não existem pedidos concluídos para refazer nesta marcação.');
        return;
      }
      setRefazerMarcacao(data.marcacao);
      setShowRefazerProposta(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao abrir Refazer';
      message.error(msg);
    } finally {
      setPreparingRefazer(false);
    }
  };

  const handleProposalSuccess = async () => {
    setShowSolicitarProposta(false);
    setShowRefazerProposta(false);
    setRefazerMarcacao(null);
    try {
      const data = await cccApi.getRecuperacaoMarcacao(id);
      if (data?.success && data.marcacao) {
        onMarcacaoPatched?.(data.marcacao);
      } else {
        await onMarcacaoRefresh?.();
        onWorkspaceNavigate?.(SUBSTAGE_AGUARDAR_PROPOSTA_BO);
      }
    } catch {
      await onMarcacaoRefresh?.();
      onWorkspaceNavigate?.(SUBSTAGE_AGUARDAR_PROPOSTA_BO);
    }
    setHistoryRefreshKey((k) => k + 1);
    setInfoTab('pedido');
    message.success('Pedido de proposta submetido. Cliente movido para "A aguardar Proposta BO".');
  };

  const handleSubmeterContrato = () => {
    if (!isMine) {
      message.warning('A marcação tem de estar atribuída a si antes de submeter contrato.');
      return;
    }
    if (!id) {
      message.error('Marcação inválida — não foi possível abrir Submeter Contrato.');
      return;
    }
    setPreparingContrato(true);
    setShowSubmeterContrato(true);
    setPreparingContrato(false);
  };

  const handleContractSuccess = async () => {
    setShowSubmeterContrato(false);
    try {
      if (normalizedSubstage !== SUBSTAGE_CONTRATO_SUBMETIDO) {
        await onSubstageChange(SUBSTAGE_CONTRATO_SUBMETIDO);
      }
      const data = await cccApi.getRecuperacaoMarcacao(id);
      if (data?.success && data.marcacao) {
        onMarcacaoPatched?.(data.marcacao);
      } else {
        await onMarcacaoRefresh?.();
      }
    } catch {
      await onMarcacaoRefresh?.();
    }
    setHistoryRefreshKey((k) => k + 1);
    message.success('Contrato submetido com sucesso.');
  };

  const handleAlterarEstado = async (newSubstage: string) => {
    if (newSubstage === substage || changingSubstage) return;
    setChangingSubstage(true);
    try {
      await onSubstageChange(newSubstage);
    } finally {
      setChangingSubstage(false);
    }
  };

  const toggleServico = (groupId: string) => {
    setExpandedServicos((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleSaveNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    await onAddNote(text);
    setNoteDraft('');
  };

  return (
    <div className="rec-lead-panel">
      <div className="rec-lead-panel-bar">
        <div className="rec-lead-panel-bar-title">
          <span className="rec-lead-panel-empresa-line">
            {showWorkspaceFormTab && workspaceFormIncomplete && (
              <RecuperacaoMotivoWarningIcon title={workspaceFormWarnTitle} />
            )}
            <span className="rec-lead-panel-empresa">{marcacao.nome_empresa || '—'}</span>
          </span>
          <span className="rec-lead-panel-meta">{id}</span>
        </div>
        <button type="button" className="rec-lead-panel-collapse" onClick={onCollapse} title="Fechar ficha">
          <span>Fechar ficha</span>
          <span className="rec-lead-panel-collapse-icon" aria-hidden>
            ▼
          </span>
        </button>
      </div>

      <div className="rec-lead-panel-body">
        <div className="rec-lead-panel-main">
          <nav className="rec-lead-info-tabs" aria-label="Origem da informação">
            {showAssinadoTab && (
              <button
                type="button"
                className={`rec-lead-info-tab ${infoTab === 'assinado' ? 'is-active' : ''}`}
                onClick={() => setInfoTab('assinado')}
              >
                Assinatura
              </button>
            )}
            {showEnvioPropostaTab && (
              <button
                type="button"
                className={`rec-lead-info-tab ${infoTab === 'envio_proposta' ? 'is-active' : ''}`}
                onClick={() => setInfoTab('envio_proposta')}
              >
                Envio da Proposta
              </button>
            )}
            {showTentativasTab && (
              <button
                type="button"
                className={`rec-lead-info-tab ${infoTab === 'tentativas_contacto' ? 'is-active' : ''}`}
                onClick={() => setInfoTab('tentativas_contacto')}
              >
                Tentativas de Contacto
                {tentativasIncomplete && (
                  <RecuperacaoMotivoWarningIcon
                    className="rec-lead-tab-warn"
                    title={TENTATIVAS_CONTACTO_WARNING_TEXT}
                  />
                )}
              </button>
            )}
            {showFidelizadosTab && (
              <button
                type="button"
                className={`rec-lead-info-tab ${infoTab === 'fidelizados' ? 'is-active' : ''}`}
                onClick={() => setInfoTab('fidelizados')}
              >
                Fidelização
                {fidelizadosIncomplete && (
                  <RecuperacaoMotivoWarningIcon
                    className="rec-lead-tab-warn"
                    title={FIDELIZADOS_WARNING_TEXT}
                  />
                )}
              </button>
            )}
            {showMotivoTab && (
              <button
                type="button"
                className={`rec-lead-info-tab ${infoTab === 'motivo' ? 'is-active' : ''}`}
                onClick={() => setInfoTab('motivo')}
              >
                Motivo
                {motivoIncomplete && (
                  <RecuperacaoMotivoWarningIcon
                    className="rec-lead-tab-warn"
                    title={
                      motivoVariant === 'pendente'
                        ? PENDENTE_MOTIVO_WARNING_TEXT
                        : motivoVariant === 'sem_interesse'
                          ? SEM_INTERESSE_MOTIVO_WARNING_TEXT
                          : MOTIVO_WARNING_TEXT
                    }
                  />
                )}
              </button>
            )}
            <button
              type="button"
              className={`rec-lead-info-tab ${infoTab === 'callcenter' ? 'is-active' : ''}`}
              onClick={() => setInfoTab('callcenter')}
            >
              CallCenter
            </button>
            {canViewHistory && (
              <button
                type="button"
                className={`rec-lead-info-tab ${infoTab === 'historico' ? 'is-active' : ''}`}
                onClick={() => setInfoTab('historico')}
              >
                Histórico
              </button>
            )}
            <button
              type="button"
              className={`rec-lead-info-tab ${infoTab === 'comercial' ? 'is-active' : ''}`}
              onClick={() => proposalExists && setInfoTab('comercial')}
              disabled={!proposalExists}
              title={
                proposalExists
                  ? 'Histórico de propostas Comercial'
                  : 'Sem propostas Comercial nesta marcação'
              }
            >
              Comercial
              {!proposalExists && <span className="rec-lead-info-tab-hint"> (sem proposta)</span>}
            </button>
            <button
              type="button"
              className={`rec-lead-info-tab ${infoTab === 'pedido' ? 'is-active' : ''}`}
              onClick={() => pedidoExists && setInfoTab('pedido')}
              disabled={!pedidoExists}
              title={
                pedidoExists
                  ? 'Pedido submetido pelo CCC e resposta BackOffice'
                  : 'Submeta um pedido com Solicitar Proposta'
              }
            >
              Pedido de Proposta
              {!pedidoExists && <span className="rec-lead-info-tab-hint"> (sem pedido)</span>}
            </button>
          </nav>

          {infoTab === 'assinado' && showAssinadoTab ? (
            <RecuperacaoAssinadoForm
              marcacao={marcacao}
              marcacaoId={id}
              initial={assinadoData}
              disabled={!isMine}
              onSaved={() => onMarcacaoRefresh?.()}
            />
          ) : infoTab === 'envio_proposta' && showEnvioPropostaTab ? (
            <RecuperacaoEnvioPropostaForm
              marcacao={marcacao}
              marcacaoId={id}
              initial={envioPropostaData}
              disabled={!isMine}
              onSaved={() => onMarcacaoRefresh?.()}
            />
          ) : infoTab === 'fidelizados' && showFidelizadosTab ? (
            <RecuperacaoFidelizadosForm
              marcacaoId={id}
              initial={fidelizadosData}
              disabled={!isMine}
              onSaved={() => onMarcacaoRefresh?.()}
            />
          ) : infoTab === 'tentativas_contacto' && showTentativasTab ? (
            <RecuperacaoTentativasContactoForm
              marcacaoId={id}
              initial={tentativasData}
              disabled={!isMine}
              onSaved={() => onMarcacaoRefresh?.()}
            />
          ) : infoTab === 'motivo' && showMotivoTab && motivoVariant ? (
            <RecuperacaoWorkspaceMotivo
              variant={motivoVariant}
              marcacaoId={id}
              initial={motivoData}
              disabled={!isMine}
              onSaved={() => onMarcacaoRefresh?.()}
            />
          ) : infoTab === 'historico' && canViewHistory ? (
            <section className="rec-history">
              <div className="rec-history-filters">
                <label className="rec-history-filter">
                  <span>Tipo de evento</span>
                  <select
                    className="rec-select"
                    value={historyTypeFilter}
                    onChange={(e) => setHistoryTypeFilter(e.target.value as HistoryCategory)}
                  >
                    <option value="all">Todos</option>
                    <option value="iniciar_caso">Início</option>
                    <option value="status_update">Atualização do Estado</option>
                    <option value="solicitar_proposta">Solicitar Proposta</option>
                    <option value="alteracao_comercial">Alteração do Comercial</option>
                  </select>
                </label>
                <label className="rec-history-filter">
                  <span>Estado relacionado</span>
                  <select
                    className="rec-select"
                    value={historyStatusFilter}
                    onChange={(e) => setHistoryStatusFilter(e.target.value)}
                  >
                    <option value="all">Todos</option>
                    {historyStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {historyLoading ? (
                <p className="rec-lead-panel-empty">A carregar histórico…</p>
              ) : filteredHistoryEvents.length === 0 ? (
                <p className="rec-lead-panel-empty">Sem eventos relevantes no histórico desta marcação.</p>
              ) : (
                <div className="rec-history-layout">
                  <div className="rec-history-horizontal">
                    {timelineRows.map((row, rowIdx) => (
                      <div
                        key={`history-row-${rowIdx}`}
                        className={`rec-history-row ${row.length <= 1 ? 'is-single' : ''}`}
                        style={{ ['--row-points' as string]: String(row.length) }}
                      >
                        <span className="rec-history-horizontal-line" aria-hidden />
                        <ol className="rec-history-points">
                          {row.map((event, index) => {
                            const statuses = historyEventStatusLabels(event);
                            const pointKey = `${event.timestamp}|${event.event_type}|${rowIdx}|${index}`;
                            return (
                              <li
                                key={`${event.timestamp}-${event.event_type}-${rowIdx}-${index}`}
                              className={`rec-history-point-wrap rec-history-node--${historyEventCategory(event)} ${
                                  index === row.length - 1 ? 'is-last' : ''
                                } ${activeHistoryKey === pointKey ? 'is-active' : ''}`}
                              >
                                <button
                                  type="button"
                                  className="rec-history-point"
                                  aria-label={`${historyEventTypeLabel(event.event_type)} em ${formatDateTime(
                                    event.timestamp
                                  )}`}
                                  onMouseEnter={() => setActiveHistoryKey(pointKey)}
                                  onFocus={() => setActiveHistoryKey(pointKey)}
                                />
                                {index < row.length - 1 && (
                                  <span className="rec-history-arrow" aria-hidden>
                                    ➜
                                  </span>
                                )}
                                <span className="rec-history-point-time">
                                  {new Date(event.timestamp).toLocaleTimeString('pt-PT', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                                <span className="rec-history-point-type">
                                  {historyEventTypeLabel(event.event_type)}
                                </span>
                                <span className="rec-history-point-status">
                                  {statuses[statuses.length - 1] || '—'}
                                </span>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    ))}
                  </div>
                  <aside className="rec-history-details">
                    {activeHistoryEvent ? (
                      <>
                        <div className="rec-history-details-head">
                          <span className={`rec-history-tag rec-history-tag--${historyEventCategory(activeHistoryEvent)}`}>
                            {historyCategoryLabel(historyEventCategory(activeHistoryEvent))}
                          </span>
                          <h5>{activeHistoryEvent.title}</h5>
                        </div>
                        <p>
                          <strong>Quando:</strong> {formatDateTime(activeHistoryEvent.timestamp)}
                        </p>
                        <p>
                          <strong>Quem:</strong> {activeHistoryEvent.by_name || 'Utilizador'}
                        </p>
                        <p>
                          <strong>Evento:</strong> {historyEventSummary(activeHistoryEvent)}
                        </p>
                      </>
                    ) : (
                      <p>Selecione um ponto para ver detalhes.</p>
                    )}
                  </aside>
                </div>
              )}
            </section>
          ) : (
            <LeadDetailsContent
              layout={activeLayout}
              expandedServicos={expandedServicos}
              onToggleServico={toggleServico}
              emptyMessage={
                infoTab === 'pedido'
                  ? 'Ainda não submeteu um pedido de proposta nesta marcação. Use Solicitar Proposta nas Ações.'
                  : infoTab === 'comercial'
                    ? 'Não há dados de proposta comercial para esta marcação.'
                    : 'Sem informação de agendamento CallCenter para esta marcação.'
              }
            />
          )}
        </div>

        <aside className="rec-lead-sidebar">
          <div className="rec-lead-workboard">
            <h4 className="rec-lead-section-title">Ações</h4>
            <div className="rec-lead-workboard-row">
              <label className="rec-lead-field">
                <span>Alterar estado</span>
                <select
                  className="rec-select"
                  value={substage}
                  disabled={changingSubstage}
                  onChange={(e) => handleAlterarEstado(e.target.value)}
                >
                  {ALL_WORKSPACE_SUBSTAGES.map((st) => (
                    <option key={st} value={st}>
                      {subsectionLabel(st)}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="rec-btn rec-btn-criar-evento"
                disabled={!isMine}
                onClick={onCreateCalendarEvent}
                title="Criar evento no calendário com este cliente"
              >
                Criar Evento
              </button>
              <div className="prosp-proposal-actions-row">
                <button
                  type="button"
                  className="rec-btn rec-btn-solicitar-proposta prosp-lead-action prosp-lead-action--half"
                  disabled={!isMine || preparingProposta || preparingRefazer}
                  onClick={() => void handleSolicitarProposta()}
                >
                  {preparingProposta ? 'A preparar…' : 'Solicitar Proposta'}
                </button>
                <button
                  type="button"
                  className="rec-btn rec-btn-refazer-proposta prosp-lead-action prosp-lead-action--half"
                  disabled={!isMine || preparingRefazer || preparingProposta}
                  onClick={() => void handleRefazerProposta()}
                >
                  {preparingRefazer ? 'A preparar…' : 'Refazer'}
                </button>
              </div>
              {(normalizedSubstage === SUBSTAGE_CONTRATO_SUBMETIDO ||
                normalizedSubstage === 'fecho-pendente-documentos' ||
                normalizedSubstage === 'pre-fecho-assinado') && (
                <button
                  type="button"
                  className="rec-btn rec-btn-submeter-contrato prosp-lead-action"
                  disabled={!isMine || preparingContrato}
                  onClick={handleSubmeterContrato}
                >
                  {preparingContrato ? 'A preparar…' : 'Submeter Contrato'}
                </button>
              )}
              <button type="button" className="rec-btn-revert" onClick={onRetroceder}>
                Re-enviar para a Pool
              </button>
              {onReverterParaComercial ? (
                <button
                  type="button"
                  className="rec-btn rec-btn-reverter-comercial"
                  onClick={onReverterParaComercial}
                >
                  Reverter para Comercial
                </button>
              ) : null}
            </div>
          </div>

          <div className="rec-lead-notes">
          <h4 className="rec-lead-section-title">Notas</h4>
          <div className="rec-lead-notes-compose">
            <textarea
              className="rec-lead-notes-input"
              rows={3}
              placeholder="Registar contacto, feedback do cliente, próximos passos…"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
            />
            <button
              type="button"
              className="rec-btn rec-btn-primary"
              disabled={savingNote || !noteDraft.trim()}
              onClick={handleSaveNote}
            >
              {savingNote ? 'A guardar…' : 'Adicionar nota'}
            </button>
          </div>
          <div className="rec-lead-notes-list">
            {notes.length === 0 ? (
              <p className="rec-lead-notes-empty">Ainda sem notas nesta marcação.</p>
            ) : (
              [...notes].reverse().map((n, i) => (
                <article key={`${n.at}-${i}`} className="rec-lead-note-card">
                  <p className="rec-lead-note-text">{n.text}</p>
                  <footer className="rec-lead-note-meta">
                    {n.by_name || 'Utilizador'} · {formatDateTime(n.at)}
                  </footer>
                </article>
              ))
            )}
          </div>
          </div>
        </aside>
      </div>

      {showSolicitarProposta && id && (
        <CccSolicitarPropostaOverlay
          mode="solicitar"
          marcacao={marcacao}
          onClose={() => setShowSolicitarProposta(false)}
          onSuccess={() => void handleProposalSuccess()}
        />
      )}

      {showRefazerProposta && id && refazerMarcacao && (
        <CccSolicitarPropostaOverlay
          mode="refazer"
          marcacao={refazerMarcacao}
          onClose={() => {
            setShowRefazerProposta(false);
            setRefazerMarcacao(null);
          }}
          onSuccess={() => void handleProposalSuccess()}
        />
      )}

      {showSubmeterContrato && id && (
        <CccSubmeterContratoRecuperacaoOverlay
          marcacao={marcacao}
          onClose={() => setShowSubmeterContrato(false)}
          onSuccess={() => void handleContractSuccess()}
        />
      )}
    </div>
  );
};

export default RecuperacaoLeadPanel;
